import { showTooltip, hideTooltip } from './Tooltip';

export type HeatDatum = { date: string; n: number };

// GitHub-style contribution heatmap: 13 colonnes (semaines) × 7 lignes (jours)
export default function Heatmap({
  data,
  cell = 13,
  gap = 3,
  onClickDay,
  activeDate,
}: {
  data: HeatDatum[];
  cell?: number;
  gap?: number;
  onClickDay?: (d: HeatDatum) => void;
  activeDate?: string | null;
}) {
  const safeHeat: HeatDatum[] = (Array.isArray(data) ? data : [])
    .filter((d): d is HeatDatum => !!d && typeof d.date === 'string');
  if (safeHeat.length === 0) return null;
  // Construit la grille en alignant sur un lundi
  const byDate = new Map(safeHeat.map((d) => [d.date, Number(d.n ?? 0)]));
  const first = new Date(safeHeat[0].date + 'T00:00:00Z');
  const dow0 = (first.getUTCDay() + 6) % 7; // lundi=0
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - dow0);
  const last = new Date(safeHeat[safeHeat.length - 1].date + 'T00:00:00Z');
  const weeks = Math.ceil(((last.getTime() - start.getTime()) / 86400_000 + 1) / 7);

  const max = Math.max(1, ...safeHeat.map((d) => Number(d.n ?? 0)));

  const cells: Array<{ w: number; d: number; date: string; n: number }> = [];
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < 7; d++) {
      const dt = new Date(start);
      dt.setUTCDate(start.getUTCDate() + w * 7 + d);
      const key = dt.toISOString().slice(0, 10);
      const n = byDate.get(key) ?? 0;
      cells.push({ w, d, date: key, n });
    }
  }

  const width = weeks * (cell + gap);
  const height = 7 * (cell + gap);
  const color = (n: number) => {
    if (n === 0) return 'var(--c-nav)';
    const t = Math.min(1, n / max);
    // var(--c-sage) ≈ #81B29A ; on interpole vers une teinte plus foncée
    const from = [245, 241, 233]; // ~nav
    const to = [63, 112, 82]; // deep sage
    const r = Math.round(from[0] + (to[0] - from[0]) * t);
    const g = Math.round(from[1] + (to[1] - from[1]) * t);
    const b = Math.round(from[2] + (to[2] - from[2]) * t);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {cells.map((c) => (
        <rect
          key={c.date}
          x={c.w * (cell + gap)}
          y={c.d * (cell + gap)}
          width={cell}
          height={cell}
          rx={2}
          className="heatmap-day"
          fill={color(c.n)}
          opacity={activeDate && activeDate !== c.date ? 0.35 : 1}
          onMouseMove={(e) =>
            showTooltip({
              x: e.clientX,
              y: e.clientY,
              title: c.date,
              rows: [{ label: 'Activités', value: String(c.n) }],
            })
          }
          onMouseLeave={hideTooltip}
          onClick={() => onClickDay?.({ date: c.date, n: c.n })}
        />
      ))}
    </svg>
  );
}
